/**
 * Thin wrapper around @microsoft/teams-js initialization and SSO.
 * All Teams-specific concerns live here so the rest of the app can
 * depend on a stable surface.
 */

import { app, authentication } from "@microsoft/teams-js";

export type TeamsTheme = "default" | "dark" | "contrast";

export interface TeamsContextSnapshot {
  theme: TeamsTheme;
  subPageId: string | null;
  tenantId: string | null;
  userPrincipalName: string | null;
}

let initPromise: Promise<boolean> | null = null;

/**
 * Initialize the Teams SDK. Resolves `true` inside Teams, `false` otherwise
 * (e.g. standalone browser during dev). Idempotent.
 */
export async function initializeTeams(): Promise<boolean> {
  if (!initPromise) {
    initPromise = (async () => {
      try {
        await app.initialize();
        return true;
      } catch {
        return false;
      }
    })();
  }
  return initPromise;
}

export async function getTeamsContext(): Promise<TeamsContextSnapshot | null> {
  const inTeams = await initializeTeams();
  if (!inTeams) return null;
  const ctx = await app.getContext();
  return {
    theme: (ctx.app.theme ?? "default") as TeamsTheme,
    subPageId: ctx.page?.subPageId ?? null,
    tenantId: ctx.user?.tenant?.id ?? null,
    userPrincipalName: ctx.user?.userPrincipalName ?? null,
  };
}

/** Request an SSO token from Teams. Throws if not in Teams or SSO fails. */
export async function getAuthToken(): Promise<string> {
  const inTeams = await initializeTeams();
  if (!inTeams) throw new Error("Not running inside Microsoft Teams");
  return authentication.getAuthToken();
}

export function onThemeChange(cb: (theme: TeamsTheme) => void): void {
  void initializeTeams().then((inTeams) => {
    if (!inTeams) return;
    app.registerOnThemeChangeHandler((theme) => cb(theme as TeamsTheme));
  });
}

export function notifyTeamsSuccess(): void {
  void initializeTeams().then((inTeams) => {
    if (inTeams) app.notifySuccess();
  });
}
